import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Camera, Loader2, Check, X, RotateCcw, Sparkles, ArrowLeft, Image as ImageIcon } from "lucide-react";

/**
 * Gasto Rápido — mobile-first quick expense capture with AI vision.
 * Flow: tap camera → snap photo → auto-OCR (Gemini Vision → Tesseract fallback)
 * → prefilled form → confirm → save to MongoDB.
 */
export default function GastoRapido() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [step, setStep] = useState("capture");  // capture | scanning | review | saving
  const [preview, setPreview] = useState(null);
  const [fileBlob, setFileBlob] = useState(null);
  const [extracted, setExtracted] = useState(null);
  const [provider, setProvider] = useState(null);
  const [form, setForm] = useState({
    supplier: "", supplier_tax_id: "", expense_date: "",
    invoice_number: "", base: 0, tax_rate: 21, tax_amount: 0, total: 0,
    category: "materiales", notes: "",
  });

  const openCamera = () => fileInputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileBlob(file);
    setPreview(URL.createObjectURL(file));
    setStep("scanning");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/expenses/scan", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const ex = data?.extracted || {};
      setExtracted(ex);
      setProvider(ex._provider);
      // Prefill the form with extracted values
      const today = new Date().toISOString().slice(0, 10);
      const base = Number(ex.base || 0);
      const total = Number(ex.total || 0);
      const taxAmt = Number(ex.tax_amount || (total && base ? total - base : 0));
      setForm({
        supplier: ex.supplier || "",
        supplier_tax_id: ex.supplier_tax_id || "",
        expense_date: (ex.expense_date && /^\d{4}-\d{2}-\d{2}$/.test(ex.expense_date)) ? ex.expense_date : today,
        invoice_number: ex.invoice_number || "",
        base: base || (total ? Number((total / (1 + 21/100)).toFixed(2)) : 0),
        tax_rate: Number(ex.tax_rate || 21),
        tax_amount: Number(taxAmt.toFixed(2)),
        total: total,
        category: ex.category || "materiales",
        notes: "",
      });
      setStep("review");
      if (ex._warning) {
        toast.warning(ex._warning, { duration: 5000 });
      } else if (ex._provider === "gemini-vision") {
        toast.success("Datos extraídos con IA ✨");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo procesar el ticket");
      setStep("capture");
      setPreview(null);
      setFileBlob(null);
    }
  };

  const recalcFromTotal = (total, rate) => {
    if (!total) return { base: 0, tax_amount: 0 };
    const base = Number((total / (1 + rate / 100)).toFixed(2));
    return { base, tax_amount: Number((total - base).toFixed(2)) };
  };

  const setTotal = (t) => {
    const totalN = Number(t) || 0;
    setForm((f) => ({ ...f, total: totalN, ...recalcFromTotal(totalN, f.tax_rate) }));
  };
  const setTaxRate = (r) => {
    const rateN = Number(r) || 0;
    setForm((f) => ({ ...f, tax_rate: rateN, ...recalcFromTotal(f.total, rateN) }));
  };

  const retake = () => {
    setStep("capture");
    setPreview(null);
    setFileBlob(null);
    setExtracted(null);
    setProvider(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const save = async () => {
    if (!form.supplier?.trim()) return toast.error("Falta el proveedor");
    if (!form.expense_date) return toast.error("Falta la fecha");
    if (!Number(form.total)) return toast.error("El importe total no puede ser 0");
    setStep("saving");
    try {
      const { data: expense } = await api.post("/expenses", form);
      // Attach the receipt photo to the expense (best effort)
      if (fileBlob) {
        try {
          const fd = new FormData();
          fd.append("file", fileBlob);
          await api.post(`/expenses/${expense.id}/file`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch { /* photo attach failure is non-fatal */ }
      }
      toast.success("Gasto guardado ✓");
      navigate("/contabilidad");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo guardar");
      setStep("review");
    }
  };

  return (
    <div className="max-w-xl mx-auto" data-testid="quick-expense-page">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4"
        data-testid="qe-back"
      >
        <ArrowLeft className="w-4 h-4" /> Volver
      </button>
      <div className="kbar inline-flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> IA · Gemini Vision</div>
      <h1 className="font-display font-extrabold text-3xl sm:text-5xl tracking-tight mt-1 mb-6">Gasto Rápido</h1>

      {step === "capture" && (
        <div className="space-y-4" data-testid="qe-capture">
          <button
            onClick={openCamera}
            className="w-full py-10 border-2 border-dashed border-slate-300 hover:border-orange-500 hover:bg-orange-50 transition flex flex-col items-center gap-3 group"
            data-testid="qe-camera-btn"
          >
            <div className="w-16 h-16 rounded-full bg-orange-600 text-white flex items-center justify-center group-hover:scale-110 transition">
              <Camera className="w-8 h-8" />
            </div>
            <div className="text-lg font-bold">Hacer foto del ticket</div>
            <div className="text-sm text-slate-500">o pulsa aquí para elegir una imagen</div>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFile}
            className="hidden"
            data-testid="qe-file-input"
          />
          <div className="text-xs text-slate-500 leading-relaxed p-4 bg-slate-50 border border-slate-200">
            <div className="font-semibold text-slate-700 mb-1">📸 Consejos para mejor lectura</div>
            • Foto directamente encima, sin sombras.<br/>
            • Enfoca los datos clave (proveedor, fecha, total).<br/>
            • Si Gemini está saturado, el sistema usa OCR local como respaldo (sin coste).
          </div>
        </div>
      )}

      {step === "scanning" && (
        <div className="space-y-4" data-testid="qe-scanning">
          {preview && (
            <img src={preview} alt="ticket" className="w-full max-h-96 object-contain border border-slate-200 bg-slate-50" />
          )}
          <div className="flex items-center gap-3 p-4 bg-orange-50 border border-orange-200 text-orange-800">
            <Loader2 className="w-5 h-5 animate-spin" />
            <div>
              <div className="font-bold">Extrayendo datos con IA…</div>
              <div className="text-xs">Suele tardar 3-6 segundos.</div>
            </div>
          </div>
        </div>
      )}

      {step === "review" && (
        <div className="space-y-4" data-testid="qe-review">
          {preview && (
            <details className="border border-slate-200 bg-slate-50">
              <summary className="p-3 text-sm cursor-pointer inline-flex items-center gap-2 select-none">
                <ImageIcon className="w-4 h-4" /> Ver foto del ticket
              </summary>
              <img src={preview} alt="ticket" className="w-full max-h-96 object-contain" />
            </details>
          )}

          {provider && (
            <div className="text-xs px-3 py-2 bg-slate-50 border border-slate-200 flex items-center gap-2" data-testid="qe-provider">
              <Sparkles className="w-3.5 h-3.5 text-orange-600" />
              {provider === "gemini-vision" && <span>Datos extraídos con <b>IA (Gemini Vision)</b>. Revísalos abajo.</span>}
              {provider === "tesseract+gemini-text" && <span>Extraído con <b>OCR local + IA texto</b>. Revísalos.</span>}
              {provider === "tesseract-only" && <span className="text-amber-700">IA saturada — datos de OCR local. Rellena manualmente los que falten.</span>}
            </div>
          )}

          <div className="grid grid-cols-1 gap-3" data-testid="qe-form">
            <label className="block">
              <div className="text-xs font-semibold text-slate-600 mb-1">Proveedor *</div>
              <input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })}
                className="w-full h-12 px-3 border border-slate-200 focus:border-orange-500 outline-none text-base"
                data-testid="qe-supplier" />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <div className="text-xs font-semibold text-slate-600 mb-1">NIF/CIF</div>
                <input value={form.supplier_tax_id} onChange={(e) => setForm({ ...form, supplier_tax_id: e.target.value })}
                  className="w-full h-12 px-3 border border-slate-200 focus:border-orange-500 outline-none text-base"
                  data-testid="qe-tax-id" />
              </label>
              <label className="block">
                <div className="text-xs font-semibold text-slate-600 mb-1">Nº factura</div>
                <input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
                  className="w-full h-12 px-3 border border-slate-200 focus:border-orange-500 outline-none text-base"
                  data-testid="qe-invoice-number" />
              </label>
            </div>
            <label className="block">
              <div className="text-xs font-semibold text-slate-600 mb-1">Fecha *</div>
              <input type="date" value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })}
                className="w-full h-12 px-3 border border-slate-200 focus:border-orange-500 outline-none text-base"
                data-testid="qe-date" />
            </label>
            <div className="grid grid-cols-3 gap-3">
              <label className="block">
                <div className="text-xs font-semibold text-slate-600 mb-1">Base (€)</div>
                <input type="number" step="0.01" value={form.base}
                  onChange={(e) => setForm({ ...form, base: Number(e.target.value) })}
                  className="w-full h-12 px-3 border border-slate-200 focus:border-orange-500 outline-none text-base tabular-nums"
                  data-testid="qe-base" />
              </label>
              <label className="block">
                <div className="text-xs font-semibold text-slate-600 mb-1">IVA %</div>
                <input type="number" step="1" value={form.tax_rate} onChange={(e) => setTaxRate(e.target.value)}
                  className="w-full h-12 px-3 border border-slate-200 focus:border-orange-500 outline-none text-base tabular-nums"
                  data-testid="qe-tax-rate" />
              </label>
              <label className="block">
                <div className="text-xs font-semibold text-slate-600 mb-1">Total (€) *</div>
                <input type="number" step="0.01" value={form.total} onChange={(e) => setTotal(e.target.value)}
                  className="w-full h-12 px-3 border-2 border-orange-500 focus:border-orange-600 outline-none text-base tabular-nums font-bold"
                  data-testid="qe-total" />
              </label>
            </div>
            <label className="block">
              <div className="text-xs font-semibold text-slate-600 mb-1">Categoría</div>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full h-12 px-3 border border-slate-200 focus:border-orange-500 outline-none text-base bg-white"
                data-testid="qe-category">
                <option value="materiales">Materiales</option>
                <option value="transporte">Transporte</option>
                <option value="herramientas">Herramientas</option>
                <option value="suministros">Suministros</option>
                <option value="otros">Otros</option>
              </select>
            </label>
            <label className="block">
              <div className="text-xs font-semibold text-slate-600 mb-1">Notas</div>
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2} placeholder="Opcional"
                className="w-full p-3 border border-slate-200 focus:border-orange-500 outline-none text-base resize-none"
                data-testid="qe-notes" />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3 pt-2 sticky bottom-4 bg-white/90 backdrop-blur">
            <button onClick={retake}
              className="h-14 flex items-center justify-center gap-2 border border-slate-300 hover:bg-slate-50 font-semibold"
              data-testid="qe-retake">
              <RotateCcw className="w-4 h-4" /> Otra foto
            </button>
            <button onClick={save}
              className="h-14 bg-orange-600 hover:bg-orange-700 text-white font-bold flex items-center justify-center gap-2"
              data-testid="qe-save">
              <Check className="w-5 h-5" /> Confirmar y guardar
            </button>
          </div>
        </div>
      )}

      {step === "saving" && (
        <div className="p-8 flex items-center justify-center gap-3 text-slate-500" data-testid="qe-saving">
          <Loader2 className="w-5 h-5 animate-spin" /> Guardando gasto…
        </div>
      )}
    </div>
  );
}
